import {uuid} from 'uuidv4';
import {float, extract} from '../utils.js';
import {sdf_types} from './sdf-types.js';
import {doc} from '../../docs.js';
import {sdf_union} from './sdf.js';
import {generateScene} from './scene-generator.js';

doc(
    "gl.sdf",
    {
        "position": "",
        "type": "",
        "sizeVec": "",
        "cornerRadius":""
    },
    "Creates sdf for a type",
    () => 2,
    {
        isHot: true,
        inlets: 4,
    });

export const gl_sdf = (object, type, size, round, transformation, rotation, index, repeats) => {
    object.needsHotReload = true;
    let id = uuid().replaceAll('-', '');
    return ([position]) => {
        let {codes, functions, variables, uniforms} = extract(position, type, size, round, transformation, rotation, index, repeats);
        if (Object.keys(codes[4]).length === 0) {
            codes[4] = undefined;
        }
        object.flash();

        let _index = codes[6];
        let a,b;
        let uv = '(1.0*gl_FragCoord.xy-vec2(width, height))/height'; 
        let fn = sdf_types[codes[1]];
        if (fn) {
            let fName = 'sdf' + id;
            functions[fName] = fn(codes);
            if (codes[1] === 'box' || codes[1] === 'sphere') {
                // is 3D
                uv = 'vec3((1.0*gl_FragCoord.xy-vec2(width, height))/height, 0.0)'; 
            }
            a = (x) => `${fName}(${x})`;
            b = `${fName}(${uv})`;
        }
       return [
            {
                id,
                code: a,
                functions,
                variables,
                uniforms
            },
            {
                id,
                code: b,
                functions,
                variables,
                uniforms
            }
        ];
    };
};


/**
 * Generates a function in glsl that is responsible for combing all the
 * SDFs together (via unions/intersects/differences) and spitting out a merged
 * SDF number to use in ray marching/drawing
 *
 * It also takes a displacement function that tells us how to change the SDF
 * value based on the (x,y,z) coordinates of the current scene pixel being drawn
 * (either through ray marching or 2D sdf drawing)
 *
 * What we want is to have a "functional" style function passed to this that
 * is essentially a function in the form of (x,y,z) => displacement, and will be
 * fed the current (x,y,z) coordinates. How can this function be represented in visual programming
 * style?
 * 
 * There is the concept of gl.uv that returns a vec2(x,y) coordinates in (-1,1) (-1,1) ranges that we 
 * use already, using the "global coordinates". This is essnetially creating a function that depends
 * on these x,y coordinates that get fed the shaders current pixel.
 *
 * What if we had a similar concept where we use gl.genXYZ that generates x,y,z arguments that can be used
 * to create a function based on these x,y,z "arguments" that can then be used in a function
 * that "knows" these x,y,z values and sets them.
 *
 * gl.genXYZ would generate a vec3 that can be used in code stemming from that code. Afterwards., we
 * feed the "code network" into a "generateFunction" node that generates an encapsulated function
 * that takes in a "vec3" object as an arguments,
 *
 *  gl.genXYZ -----> gl.unpack -----> sin(5*x)*sin(5*y)*sin(5*z) ----- > [gl.genFunction displacement] ----> [SDFManager]
 * 
 * So SDFManager will take in a "function" as an argument for displacement and use that to calculate the displacement
 * 
 *
 */
doc(
    'gl.sdfScene',
    {
        'list of sdfs': '',
        'type': '',
        'morph': '',
        'offset': '',
        'operations list': '',
    },
    "Creates a scene out of all the sdfs",
);

export const gl_sdf_scene = (object, type, morph, offset, sdfOperations) => {
    let id = uuid().replaceAll('-', '');
    let fName = 'scene' + id;
    return ([sdfs]) => {
        let functions = {};
        let uniforms = {};
        let variables = {};
        let _morph = {code: "1.0"};
        if (morph && morph() !== undefined) {
            _morph = morph();
            functions = _morph.functions;
            variables = _morph.variables;
            uniforms = _morph.uniforms;
        };

        let _offset = {code: "0.0"};
        if (offset && offset() !== undefined) {
            _offset = offset();
            functions = {... functions, ..._offset.functions};
            variables = {... variables, ..._offset.variables};
            uniforms = {... uniforms, ... _offset.uniforms};
        };


        for (let sdf of sdfs) {
            variables = {... variables, ... sdf.variables};
            uniforms = {... uniforms, ... sdf.uniforms};
            functions = {... functions, ... sdf.functions};
        }

        if (_morph.functions) {
            functions = {
                ... _morph.functions,
                ... functions
            };
        }

        if (_offset.functions) {
            functions = {
                ... _offset.functions,
                ... functions
            };
        }


        uniforms = {
            ... uniforms,
            ... _morph.uniforms,
            ... _offset.uniforms
        };

        let argType = type() || 'vec3';

        let offsetEval = _offset.code;
        if (_offset.codeEvaluator) {
            // look at the function and see how many arguments are in it
            let offsetFn = _offset.functions[Object.keys(_offset.functions)[0]];
            let numArgs = Object.keys(offsetFn.args).length;
            if (numArgs === 2) {
                // if there are 2 arguments then we need to pass the scene
                offsetEval = _offset.codeEvaluator('p, scene');
            } else {
                offsetEval = _offset.codeEvaluator('p');
            }
        }
        functions[fName] = {
            args: {
                "p": argType,
            },
            returnType: "vec2",
            body: `
vec2 scene = vec2(0.0, -1.0);
${sceneSDF2(sdfs, 'p', sdfOperations())};
return  vec2(scene.x * (${_morph.codeEvaluator ? _morph.codeEvaluator('p') : _morph.code}) + 
(${offsetEval}), scene.y);
`
        };
        let code = (p) => fName+'(' + p + ')';
        return {
            code,
            functions,
            uniforms,
            variables
        };
    };
};

const sceneSDF2 = (sdfs, input, operations) => {
    let x = generateScene(sdfs.map(x => x.code(input)), true, operations);
    return x;
};



